import React, { FunctionComponent, RefObject, useEffect, useState } from 'react';
import { getDefaultCenterFromSearch } from '@features/eligibility/select/helper';
import {
  buildSearchQueryByInfos,
  loadIPEAndBANAddresses,
  selectIPEInfos,
  selectBANInfos,
  selectLastApiError,
  selectSearchInfos,
  setIPE,
  setBAN,
  setSearchType
} from './reducer/geoMatchSlice';
import { IDataCoordinates } from '@features/eligibility/interfaces';
import { geoMatchAddress } from '@features/geomatch/geoMatchInterfaces';
import { trackingPages, useMatomoHarmonizer } from '../../utils/matomoHarmonizer';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

import GeoMatchAddressesCard from '@features/geomatch/geoMatchAddressesCard';
import { Alert } from 'react-bootstrap';
import Button from '@designSystem/Button';
import GeoMatchGoogleMap from './geoMatchGoogleMap';
import ConfirmationModal from './geoMatchConfirmationModal';
import HeaderCardCustom from '@features/eligibility/HeaderCardCustom';
import LinkOrAnchor from '@designSystem/LinkOrAnchor';
import SelectSiteHeader from '@features/eligibility/SelectSiteHeader';
import Spinner from '@designSystem/Spinner';
import { scrollIntoViewIfNeeded } from '../../utils';
import { useTranslation } from 'react-i18next';

import {
  StyledAddressListCards,
  StyledAddressListCard,
  StyledIPEAddressesCards,
  StyledBANAddressesCards,
  SelectMismatchCustomWrapper,
  SelectMismatchCustomCard,
  SelectMismatchCustomMap,
  SelectMismatchMapWrapper,
  StyledSelectMismatchFooter,
  StyledSelectMismatchCardBodyContainer,
  SelectMismatchLegendPlot,
  SelectMismatchLegendElement,
  SelectMismatchLegend
} from './styles/SelectMismatchStyles';

const SelectMismatch: FunctionComponent<{
  nextStep: () => void;
  previousStep: () => void;
  pageTracking?: trackingPages;
}> = ({ nextStep, previousStep, pageTracking }) => {
  const dispatch = useAppDispatch();

  const searchInfos = useAppSelector(selectSearchInfos);
  const selectedIPE = useAppSelector(selectIPEInfos);
  const selectedBAN = useAppSelector(selectBANInfos);
  const lastApiError = useAppSelector(selectLastApiError);

  const [centerChanged, setCenterChanged] = useState<{
    lng: number;
    lat: number;
    radius?: number;
  } | null>();
  const [loading, setLoading] = useState(false);
  const [refs, setRefs] = useState<(RefObject<HTMLDivElement> | null | undefined)[]>();
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState<geoMatchAddress[]>([]);
  const [centerMap, setCenterMap] = useState<{ lng: number; lat: number }>({ lng: 0, lat: 0 });
  const [validated, setValidated] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const trackEvent = useMatomoHarmonizer();

  const searchDataAsCoordinate = searchInfos.data as IDataCoordinates;

  // ID is not unique, we use identifiant_immeuble instead
  function setIPEWithId(addressId: string) {
    const selected = addresses.find((e) => e.identifiant_immeuble === addressId);
    dispatch(setIPE({ address: selected }));
  }

  function setBANWithId(addressId: string) {
    const selected = addresses.find((e) => e.id === addressId);
    dispatch(setBAN({ address: selected }));
  }

  const reloadData = (coords: { lat: number; lng: number }) => {
    dispatch(
      setSearchType({
        type: 'coordinates',
        data: {
          latitude: coords.lat,
          longitude: coords.lng,
          radius: centerChanged?.radius || searchDataAsCoordinate.radius || '200'
        }
      })
    );
    setCenterChanged(null);
    if (pageTracking)
      trackEvent({
        page: pageTracking,
        category: 'Choix_Site',
        actionType: 'Rafraîchir',
        actionDetails: 'Carte'
      });
  };

  const setMapCenter = (
    centerPoint: { latitude: number; longitude: number },
    forceData: boolean
  ) => {
    // Center map by info or with response
    const defaultCenter = getDefaultCenterFromSearch(searchInfos);
    if (defaultCenter && !forceData) {
      setCenterMap(defaultCenter);
    } else if (centerPoint) {
      setCenterMap({
        lng: centerPoint.longitude,
        lat: centerPoint.latitude
      });
    }
  };

  useEffect(() => {
    if (searchInfos.type) {
      const queryParam = buildSearchQueryByInfos(searchInfos);
      setLoading(true);

      dispatch(loadIPEAndBANAddresses(queryParam)).then((res) => {
        if (res.payload) {
          const data = res.payload.map((ga: geoMatchAddress, index: number) => {
            const newData = { ...ga };

            newData.index = index + 1;
            return newData;
          });

          setMapCenter(data[0], false);
          setAddresses(data);
          if (data.length === 0) {
            if (pageTracking)
              trackEvent({
                page: pageTracking,
                category: 'Choix_Site',
                actionType: 'Aucun',
                actionDetails: 'Site_Disponible'
              });
          }

          const newRefs = data.reduce((obj: { [id: string]: string }, item: geoMatchAddress) => {
            return {
              ...obj,
              [item['index']]: React.createRef()
            };
          }, {});
          setRefs(newRefs);
        }
        setLoading(false);
      });
    }
  }, [searchInfos.type, searchInfos.data]);

  useEffect(() => {
    const id = selectedIPE?.index;

    if (selectedIPE && refs && id && refs[id]) {
      const target = refs[id]?.current;

      setMapCenter(selectedIPE, true);
      if (target) {
        scrollIntoViewIfNeeded(target);
      }
    }
  }, [selectedIPE]);

  useEffect(() => {
    const id = selectedBAN?.index;

    if (selectedBAN && refs && id && refs[id]) {
      const target = refs[id]?.current;

      setMapCenter(selectedBAN, true);
      if (target) {
        scrollIntoViewIfNeeded(target);
      }
    }
  }, [selectedBAN]);

  useEffect(() => {
    if (selectedIPE && selectedBAN && validated) {
      nextStep();
    }
  }, [validated]);

  const refreshMap = () => {
    if (centerChanged) reloadData(centerChanged);
    if (pageTracking)
      trackEvent({
        page: pageTracking,
        category: 'Choix_Site',
        actionType: 'Clic',
        actionDetails: 'Button_Refresh_Map'
      });
  };

  const isSelected = (address: geoMatchAddress): boolean => {
    if (address && address.origine_type === 'IPE') {
      return selectedIPE?.identifiant_immeuble === address.identifiant_immeuble;
    } else if (address && address.origine_type === 'BAN') {
      return selectedBAN?.id === address.id;
    }
    return false;
  };

  const validate = (address: geoMatchAddress): void => {
    if (address && address.origine_type === 'IPE') {
      dispatch(setIPE({ address: address }));
    } else if (address && address.origine_type === 'BAN') {
      dispatch(setBAN({ address: address }));
    }
  };

  return (
    <SelectMismatchCustomWrapper>
      <SelectMismatchCustomCard
        header={
          <HeaderCardCustom
            titleHeader={t('features.eligibility.placeSelected', 'Place selected')}
            subTitleHeader={t(
              'features.eligibility.switchEdit',
              'You can switch the selected place by clicking “Edit”.'
            )}
          />
        }
        footer={
          <StyledSelectMismatchFooter>
            <Button design="white" onClick={previousStep}>
              {t('component.button.previousStep', 'Previous Step')}
            </Button>
            <Button
              disabled={!selectedIPE?.id || !selectedBAN?.id}
              onClick={() => {
                setShowConfirmationModal(true);
              }}
              data-testid={'select-site_next-step'}
            >
              {t('component.button.nextStep', 'Next step')}
            </Button>
          </StyledSelectMismatchFooter>
        }
      >
        <SelectSiteHeader search={searchInfos} previousStep={previousStep} />
        <StyledSelectMismatchCardBodyContainer>
          {loading && <Spinner spinnerSize={2} center />}
          {!loading && !addresses.length && (
            <>
              <Alert variant="danger" className="text-center">
                {lastApiError || t('features.geomatch.noReturnedAddress', 'No address found')}
              </Alert>
              <LinkOrAnchor onClick={previousStep}>
                {t('features.eligibility.returnSearch', 'Click here to return to search form')}
              </LinkOrAnchor>
            </>
          )}
          <StyledAddressListCards>
            <SelectMismatchLegend>
              <SelectMismatchLegendElement>
                <SelectMismatchLegendPlot color="#1567ed" />
                {t('features.geomatch.IPELegend', 'IPE Address')}
              </SelectMismatchLegendElement>
              <SelectMismatchLegendElement>
                <SelectMismatchLegendPlot color="#14bf86" />
                {t('features.geomatch.BANLegend', 'Certified BAN Address')}
              </SelectMismatchLegendElement>
            </SelectMismatchLegend>
            <StyledIPEAddressesCards>
              {addresses
                .filter((address) => address.origine_type === 'IPE')
                .map((address: geoMatchAddress, index) => (
                  <StyledAddressListCard
                    key={index}
                    data-testid={`address-card ${address.id}`}
                    ref={refs ? refs[address.index] : null}
                  >
                    {
                      <GeoMatchAddressesCard
                        address={address}
                        selected={isSelected(address)}
                        validate={validate}
                      />
                    }
                  </StyledAddressListCard>
                ))}
            </StyledIPEAddressesCards>
            <StyledBANAddressesCards>
              {addresses
                .filter((address) => address.origine_type === 'BAN')
                .map((address: geoMatchAddress, index) => (
                  <StyledAddressListCard
                    key={index}
                    data-testid={`address-card ${address.id}`}
                    ref={refs ? refs[address.index] : null}
                  >
                    {
                      <GeoMatchAddressesCard
                        address={address}
                        selected={isSelected(address)}
                        validate={validate}
                      />
                    }
                  </StyledAddressListCard>
                ))}
            </StyledBANAddressesCards>
          </StyledAddressListCards>
        </StyledSelectMismatchCardBodyContainer>
      </SelectMismatchCustomCard>
      <SelectMismatchCustomMap>
        {centerChanged && (
          <Button onClick={refreshMap}>
            {t('features.eligibility.clickToRefresh', 'Click here to refresh')}
          </Button>
        )}
        <SelectMismatchMapWrapper>
          <GeoMatchGoogleMap
            center={centerMap}
            markers={addresses}
            searchCenter={searchInfos.data as IDataCoordinates}
            searchType={searchInfos.type}
            selectedIPE={selectedIPE || undefined}
            setSelectedIPE={(addressId: string) => setIPEWithId(addressId)}
            selectedBAN={selectedBAN || undefined}
            setSelectedBAN={(addressId: string) => setBANWithId(addressId)}
            setCenterChanged={setCenterChanged}
            pageTracking={pageTracking}
          />
        </SelectMismatchMapWrapper>
      </SelectMismatchCustomMap>
      {showConfirmationModal && (
        <ConfirmationModal
          handleClose={() => setShowConfirmationModal(false)}
          confirm={() => setValidated(true)}
          IPEaddress={selectedIPE}
          BANaddress={selectedBAN}
        />
      )}
    </SelectMismatchCustomWrapper>
  );
};

export default SelectMismatch;
